





















import { Prop, Model, Component, Vue, Mixins, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import TileComponentMixin from '@/mixins/TileComponentMixin';
import { BModal } from 'bootstrap-vue';
import _ from 'lodash';
import { IN2KWindEvent, N2KWindEventReference, N2KHeadingEventReference } from '@/common/apis/DeviceApi';
import { RadialGauge } from 'canvas-gauges';
import UserMixin from '@/mixins/UserMixin';

export interface IWindTileConfiguration {
    reference?: N2KWindEventReference;
}


@Component({
    components: {
    },
})
export default class WindTile extends Mixins(TileComponentMixin, UserMixin) {
    @Prop({required: false})
    public configuration?: IWindTileConfiguration;

    public editConfiguration: IWindTileConfiguration | null = null;

    public fullscreen: boolean = false;


    public get available(): boolean {
        if (this.isPicker && !this.lastEvent) {
            return false;
        }
        return true;
    }

    public get lastEvent(): IN2KWindEvent | null {
        let data = this.getWind(this.deviceNumber, N2KWindEventReference.Apparent);
        if (!data && this.isDemo) {
            data = this.demoData;
        }
        return data;
    }

    public get windAll(): {[key in N2KWindEventReference]?: IN2KWindEvent} | null {
        return this.getWindAll(this.deviceNumber);
    }

    public get speed(): string {
        if (this.lastEvent && typeof this.lastEvent.speed === 'number') {
            return (Math.round(this.lastEvent.speed * 10) / 10).toFixed(1);
        }
        return '--';
    }
    public get angle(): string {
        if (this.lastEvent && typeof this.lastEvent.angle === 'number') {
            return (Math.round(this.lastEvent.angle * 10) / 10).toFixed(1);
        }
        return '--';
    }

    public get ageInSeconds(): number {
        if (this.lastEvent && this.lastEvent.timestamp) {
            const timestamp = typeof this.lastEvent.timestamp === 'object'
                ? this.lastEvent.timestamp
                : new Date(this.lastEvent.timestamp);

            return (new Date().getDate() - timestamp.getDate()) / 1000;
        }
        return 999;
    }

    private chart?: RadialGauge;
    private chartOptions?: CanvasGauges.RadialGaugeOptions;
    private redrawCount: number = 0;

    @Getter('n2k/wind')
    private getWind: (deviceNumber: string, reference: N2KWindEventReference) => IN2KWindEvent | null;
    @Getter('n2k/windAll')
    private getWindAll: (deviceNumber: string) => {[key in N2KWindEventReference]?: IN2KWindEvent} | null;

    private demoData: IN2KWindEvent | null = null;
    private demoDataInterval: number;

    public save() {
        this.$emit('save', this.editConfiguration);
    }

    public async mounted() {
        if (this.isPicker) {
            this.editConfiguration = {
            };
        } else {
            if (this.isDemo) {
                this.generateDemoData();
                this.demoDataInterval = setInterval(this.generateDemoData, 1000);
            }
            this.chartOptions = {
                renderTo: this.$refs.canvas as HTMLElement,
                value: 0,
                minValue: 0,
                maxValue: 360,
                width: 200,
                height: 200,
                strokeTicks: false,
                colorPlate: 'transparent',
                colorBar: '#CCC',

                // Ticks
                colorMajorTicks: '#aaa',
                colorMinorTicks: '#aaa',
                majorTicks: ['0°', '45°', '90°', '135°', '180°', '135°', '90°', '45°', '0°'],
                minorTicks: 9,
                ticksAngle: 360,
                startAngle: 180,
                // Numbers
                fontNumbersSize: 20,
                colorNumbers: '#FFF',
                numbersMargin: 2,
                needleCircleInner: false,
                 // Circle (middle of the gauge)
                needleCircleOuter: false,
                /// Needle
                colorNeedle: 'rgba(255,255,255,1)',
                colorNeedleEnd: '#fff',
                animation: false,
                valueBox: false,
                valueTextShadow: false,
                needleType: 'line',
                needleStart: 60,
                needleEnd: 105,
                needleWidth: 3,
                borders: true,
                borderInnerWidth: 0,
                borderMiddleWidth: 0,
                borderOuterWidth: 2,
                highlights: [
                    {
                        from: 5.00,
                        to: 45.00,
                        color: '#94EE88',
                    },
                    {
                        from: 315.00,
                        to: 355.00,
                        color: '#EE9488',
                    },
                ],
            };

            if (this.chartOptions && this.lastEvent && this.lastEvent.angle !== undefined) {
                this.chartOptions.value = this.lastEvent.angle;
            }

            this.chart = new RadialGauge(_.cloneDeep(this.chartOptions)).draw();
        }
    }

    public async beforeDestroy() {
        clearInterval(this.demoDataInterval);
        if (this.chart && this.chartOptions) {
            this.chart.destroy();
        }
    }



    @Watch('lastEvent')
    public updateChartValue() {
        if (this.chart && this.lastEvent && this.lastEvent.angle !== undefined) {
            this.chart.value = this.lastEvent.angle;
        }

        // Destry & Rebuild to avoid canvas crash on iOS
        this.redrawCount++;
        if (this.redrawCount > 500) {
            this.redrawCount = 0;
            if (this.chart && this.chartOptions) {
                this.chart.destroy();
                if (this.chartOptions && this.lastEvent && this.lastEvent.angle !== undefined) {
                    this.chartOptions.value = this.lastEvent.angle;
                }
                this.chart = new RadialGauge(_.cloneDeep(this.chartOptions)).draw();
            }
        }
    }


    private calculate(value: number, move: number, min: number, max: number, round: boolean): number {
        value += Math.random() * move * 2 - move;
        if (value > max) {
            value = round ? min : max;
        }
        if (value < min) {
            value = round ? max : min;
        }
        return value;
    }

    private generateDemoData() {
        const speed = this.calculate((this.demoData ? this.demoData.speed : undefined) || 5, 0.3, 2, 15, false);
        const angle = this.calculate((this.demoData ? this.demoData.angle : undefined) || (Math.random() * 360), 4, 0, 360, true);

        this.demoData = {
            speed,
            angle,
            reference: N2KWindEventReference.Magnetic,
            sensorNumber: 0,
            deviceNumber: this.deviceNumber,
            timestamp: new Date(),
        };
    }

}
